import React, { useState, useEffect, useCallback } from 'react';
import {
    Modal, Box, Typography, TextField, Button,
    List, ListItem, ListItemText, IconButton, Paper, Select, MenuItem,
    FormControl, InputLabel, Grid, CircularProgress, Alert, Tooltip,
    Accordion, AccordionSummary, AccordionDetails, Divider
} from '@mui/material';
import {
    ExpandMore, Edit, Delete, Save, Cancel, Add, GolfCourse, Business,
    FindInPage // Icon for discovery trigger
} from '@mui/icons-material';
// Remove regular axios import since we'll use the authenticated instance
// import axios from 'axios';

// --- Import the CourseDiscoveryTool component ---
import CourseDiscoveryTool from './CourseDiscoveryTool'; // Assuming it's in the same directory

// --- Define supported systems ---
const SUPPORTED_BOOKING_SYSTEMS = [
    { key: 'foreup', label: 'ForeUp Software' },
    { key: 'chronogolf', label: 'ChronoGolf' },
    { key: 'teeitup', label: 'TeeItUp' },
    { key: 'teeon', label: 'TeeOn' },
    { key: 'clubprophet', label: 'Club Prophet' },
    { key: 'clubunity', label: 'Total Club Unity' },
    { key: 'quick18', label: 'Quick18' },
];
// ------------------------------

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%', // Increased width for better layout
    maxWidth: 900, // Increased max width
    bgcolor: 'background.paper',
    border: '1px solid #ccc',
    borderRadius: '8px',
    boxShadow: 24,
    p: 4,
    maxHeight: '90vh',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column', // Ensure content flows vertically
};

const formPaperStyle = {
    p: 2,
    mb: 2,
    border: '1px dashed',
    borderColor: 'primary.main'
};

// Helper to safely stringify JSON for display
const safeJsonStringify = (obj) => {
    try {
        return JSON.stringify(obj || {}, null, 2); // Pretty print
    } catch (e) {
        console.warn("Failed to stringify JSON:", obj, e);
        return '{}';
    }
};

// Helper to safely parse JSON from input
const safeJsonParse = (str, fallback = null) => {
    try {
        if (!str || str.trim() === '') return fallback || {}; // Return empty object or fallback if string is empty/whitespace
        return JSON.parse(str);
    } catch (e) {
        console.warn("Failed to parse JSON:", str, e);
        return fallback; // Return fallback (often null to indicate error)
    }
};


function ClubForm({ club, onSave, onCancel, loading, error }) {
    const [formData, setFormData] = useState({
        id: club?.id || null,
        name: club?.name || '',
        // NEW: Add booking_system_type
        booking_system_type: club?.booking_system_type || '', // Default to empty string
        api_identifier: club?.api_identifier || '',
        api_details: safeJsonStringify(club?.api_details)
    });
    const [jsonError, setJsonError] = useState('');
    const [validationError, setValidationError] = useState('');


    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        setValidationError(''); // Clear validation errors on change
        if (name === 'api_details') {
            setJsonError(''); // Clear JSON error on change
        }
    };

    const handleSave = () => {
        setValidationError('');
        setJsonError('');
        const apiDetailsParsed = safeJsonParse(formData.api_details);
        if (apiDetailsParsed === null && formData.api_details.trim() !== '{}' && formData.api_details.trim() !== '') {
            setJsonError('Invalid JSON format for Club API Details.');
            return;
        }

        // Ensure required fields are present
        if (!formData.name || formData.name.trim() === '') {
             setValidationError('Club Name is required.');
             return;
        }
         // Optional: Validate booking system selection if needed
        // if (!formData.booking_system_type) {
        //     setValidationError('Booking System Type is required.');
        //     return;
        // }


        const payload = {
            name: formData.name.trim(), // Trim whitespace
            booking_system_type: formData.booking_system_type || null,
            api_identifier: formData.api_identifier || null, // Send null if empty
            api_details: apiDetailsParsed,
        };
        onSave(formData.id, payload);
    };

    // Update form data if the club prop changes (e.g., switching edit targets)
    useEffect(() => {
        setFormData({
            id: club?.id || null,
            name: club?.name || '',
            booking_system_type: club?.booking_system_type || '', // Load from club prop
            api_identifier: club?.api_identifier || '',
            api_details: safeJsonStringify(club?.api_details)
        });
        setJsonError(''); // Clear error when club changes
        setValidationError(''); // Clear validation errors
    }, [club]);


    return (
        <Paper sx={formPaperStyle}>
            <Typography variant="h6" gutterBottom>{formData.id ? 'Edit Club' : 'Add New Club'}</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {/* Display combined errors */}
            {(validationError || jsonError) && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {validationError} {jsonError}
                </Alert>
            )}
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        label="Club Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        fullWidth margin="dense" required autoFocus
                        disabled={loading}
                        error={validationError.includes('Club Name')}
                        helperText={validationError.includes('Club Name') ? 'Club Name is required.' : ''}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth margin="dense">
                        <InputLabel id={`club-booking-system-type-label-${formData.id || 'new'}`}>Booking System</InputLabel>
                        <Select
                            labelId={`club-booking-system-type-label-${formData.id || 'new'}`}
                            label="Booking System"
                            name="booking_system_type"
                            value={formData.booking_system_type || ''}
                            onChange={handleChange}
                            disabled={loading}
                        >
                            <MenuItem value=""><em>None / Manual / Unknown</em></MenuItem>
                            {SUPPORTED_BOOKING_SYSTEMS.map((system) => (
                                <MenuItem key={system.key} value={system.key}>{system.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                 <Grid item xs={12} sm={6}>
                     <TextField
                         label="Club API Identifier (Optional)"
                         name="api_identifier"
                         value={formData.api_identifier || ''} // Ensure controlled input
                         onChange={handleChange}
                         fullWidth margin="dense" // Changed to dense
                         helperText="Identifier needed for the selected Booking System (e.g., Facility ID, Club ID)"
                         disabled={loading}
                     />
                 </Grid>
                  <Grid item xs={12}>
                    <TextField
                        label="Club API Details (JSON, Optional)"
                        name="api_details"
                        value={formData.api_details}
                        onChange={handleChange}
                        fullWidth margin="dense" multiline rows={3} // Changed to dense, reduced rows
                        placeholder={'{\n  "extra_config": "value"\n}'}
                        helperText="JSON containing extra keys needed by the adapter (e.g., API keys, website IDs)."
                        error={!!jsonError}
                        disabled={loading}
                    />
                 </Grid>
            </Grid>
            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <Button startIcon={<Save />} onClick={handleSave} variant="contained" disabled={loading || !!validationError || !!jsonError || !formData.name}>Save Club</Button>
                <Button startIcon={<Cancel />} onClick={onCancel} variant="outlined" disabled={loading}>Cancel</Button>
            </Box>
        </Paper>
    );
}

// --- CourseForm (remains largely the same, but ensure it handles potential inherited type display if needed) ---
function CourseForm({ course, clubId, parentClubBookingSystem, onSave, onCancel, loading, error }) { // Added parentClubBookingSystem prop
    const [formData, setFormData] = useState({
        id: course?.id || null,
        club_id: course?.club_id || clubId || '', // Pre-fill club_id if adding
        name: course?.name || '',
        holes: course?.holes || 18,
        // If editing, use course's override; if adding, default to empty (inherit)
        booking_system_type: course?.booking_system_type || '',
        api_identifier: course?.api_identifier || '',
        api_details: safeJsonStringify(course?.api_details),
    });
    const [jsonError, setJsonError] = useState('');
    const [validationError, setValidationError] = useState('');

    // Calculate the effective type for display purposes
    const effectiveBookingSystemType = formData.booking_system_type || parentClubBookingSystem || '';
    const effectiveSystemLabel = SUPPORTED_BOOKING_SYSTEMS.find(s => s.key === effectiveBookingSystemType)?.label || effectiveBookingSystemType;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setValidationError(''); // Clear validation errors on change
        if (name === 'api_details') {
            setJsonError(''); // Clear JSON error on change
        }
    };

    const handleSave = () => {
         setValidationError(''); // Clear previous validation errors
         setJsonError(''); // Clear previous json errors

        const apiDetailsParsed = safeJsonParse(formData.api_details);
        if (apiDetailsParsed === null && formData.api_details.trim() !== '{}' && formData.api_details.trim() !== '') {
            setJsonError('Invalid JSON format for Course API Details.');
            return;
        }

        const payload = {
            name: formData.name.trim(),
            club_id: parseInt(formData.club_id, 10), // Should be valid if passed correctly
            holes: parseInt(formData.holes, 10),
            // Send the explicit override value (or null if empty string)
            booking_system_type: formData.booking_system_type || null,
            api_identifier: formData.api_identifier || null, // Send null if empty
            api_details: apiDetailsParsed
        };

        // Basic validation before calling onSave
        let errors = [];
        if (!payload.name) {
            errors.push("Course name is required.");
        }
        if (isNaN(payload.holes) || payload.holes <= 0 || payload.holes > 100) { // Added upper bound check
            errors.push("Number of holes must be between 1 and 100.");
        }
        if (isNaN(payload.club_id)) {
            errors.push("Invalid Club ID."); // Should not happen if pre-filled correctly
        }
        // Note: No validation needed for booking_system_type on the course form itself,
        // as empty means inherit from club.

        if (errors.length > 0) {
             setValidationError(errors.join(' '));
            return;
        }

        onSave(formData.id, payload);
    };

     // Update form data if the course/clubId prop changes
     useEffect(() => {
        setFormData({
            id: course?.id || null,
            club_id: course?.club_id || clubId || '',
            name: course?.name || '',
            holes: course?.holes || 18,
            booking_system_type: course?.booking_system_type || '', // Represents the override
            api_identifier: course?.api_identifier || '',
            api_details: safeJsonStringify(course?.api_details),
        });
        setJsonError(''); // Clear error when course/clubId changes
        setValidationError(''); // Clear validation errors
    }, [course, clubId]);

    return (
        <Paper sx={{ ...formPaperStyle, borderColor: 'secondary.main' }}>
            <Typography variant="h6" gutterBottom>{formData.id ? 'Edit Course' : 'Add New Course'}</Typography>
             {/* Display combined errors */}
             {(error || validationError || jsonError) && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                       {error} {validationError} {jsonError}
                  </Alert>
             )}

            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        label="Course Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        fullWidth margin="dense" required // Changed to dense
                        disabled={loading}
                        autoFocus // Focus on name field when form appears
                         error={validationError.includes('Course name')}
                         helperText={validationError.includes('Course name') ? 'Course Name is required.' : ''}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Holes"
                        name="holes"
                        type="number"
                        inputProps={{ min: 1, max: 100 }}
                        value={formData.holes}
                        onChange={handleChange}
                        fullWidth margin="dense" required // Changed to dense
                        disabled={loading}
                         error={validationError.includes('holes must be')}
                         helperText={validationError.includes('holes must be') ? 'Must be between 1 and 100.' : ''}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth margin="dense"> {/* Changed to dense */}
                        <InputLabel id={`course-booking-system-type-label-${formData.id || 'new'}`}>Booking System Type (Override)</InputLabel>
                        <Select
                            labelId={`course-booking-system-type-label-${formData.id || 'new'}`}
                            label="Booking System Type (Override)"
                            name="booking_system_type"
                            value={formData.booking_system_type || ''} // Controlled input for override
                            onChange={handleChange}
                            disabled={loading}
                            displayEmpty // Allows the "Inherit" option to show correctly when value is ''
                        >
                            {/* Display effective system */}
                            <MenuItem value="" disabled={!parentClubBookingSystem}>
                                <em>Inherit from Club ({effectiveSystemLabel || 'None Set'})</em>
                            </MenuItem>
                            {SUPPORTED_BOOKING_SYSTEMS.map((system) => (
                                <MenuItem key={system.key} value={system.key}>{system.label}</MenuItem>
                            ))}
                        </Select>
                         <InputLabel shrink={!!formData.booking_system_type || !parentClubBookingSystem} sx={{fontSize: '0.75rem', transform: 'translate(14px, -9px) scale(0.75)'}}>
                             Overrides Club ({effectiveSystemLabel || 'None Set'})
                         </InputLabel>
                         {/* Removed helper text as label explains override */}
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Course API Identifier (Optional)"
                        name="api_identifier"
                        value={formData.api_identifier || ''} // Ensure value is never null/undefined for controlled input
                        onChange={handleChange}
                        fullWidth margin="dense" // Changed to dense
                        helperText="ID specific to this course within the booking system (e.g., '1', 'North Course', '20519')"
                        disabled={loading}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Course API Details (JSON, Optional)"
                        name="api_details"
                        value={formData.api_details}
                        onChange={handleChange}
                        fullWidth margin="dense" multiline rows={3} // Changed to dense
                        placeholder='e.g., {"override_key": "value"}'
                        helperText="JSON details for this course (overrides club details if keys conflict)."
                        error={!!jsonError}
                        disabled={loading}
                    />
                </Grid>
            </Grid>
            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                 <Button
                     startIcon={<Save />}
                     onClick={handleSave}
                     variant="contained"
                     color="secondary"
                     // Disable if loading, or if validation/json errors exist, or if name/holes are invalid
                     disabled={loading || !!validationError || !!jsonError || !formData.name || !formData.holes }
                 >
                    Save Course
                 </Button>
                <Button startIcon={<Cancel />} onClick={onCancel} variant="outlined" disabled={loading}>Cancel</Button>
            </Box>
        </Paper>
    );
}


function AdminModal({ open, onClose, authenticatedAxios, onLogout }) {
    // Added authenticatedAxios and onLogout props
    const [clubs, setClubs] = useState([]);
    const [courses, setCourses] = useState({}); // Use object: { clubId: [course1, course2] }
    const [loading, setLoading] = useState(false); // General loading (fetch, delete)
    const [savingLoading, setSavingLoading] = useState(false); // Specific loading for save/add/update actions
    const [error, setError] = useState(''); // General errors (fetch, delete, bulk add)
    const [expandedClub, setExpandedClub] = useState(null); // Track which club accordion is open

    // --- Editing State ---
    const [editingClubId, setEditingClubId] = useState(null); // null | 'new_club' | clubId
    const [editingCourseInfo, setEditingCourseInfo] = useState(null); // null | { clubId, courseId: 'new_course' | courseId }

    // Store specific errors for forms to display them inline
    const [clubFormError, setClubFormError] = useState('');
    const [courseFormError, setCourseFormError] = useState('');

    // --- Discovery State ---
    const [discoveringForClubId, setDiscoveringForClubId] = useState(null); // Which club's discovery tool is open?

    // Combine loading states for disabling elements where needed
    const isAnyLoading = loading || savingLoading;

    // Handle authentication errors
    const handleAuthError = (err) => {
        if (err.response && err.response.status === 401) {
            // Token expired or invalid
            alert('Your session has expired. Please log in again.');
            // Call the onLogout function passed from parent
            if (onLogout) {
                onLogout();
                onClose();
            }
            return true; // Auth error was handled
        }
        return false; // Not an auth error
    };

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(''); setClubFormError(''); setCourseFormError('');
        setDiscoveringForClubId(null); // Reset discovery on full refresh
        try {
            const [clubsResponse, coursesResponse] = await Promise.all([
                authenticatedAxios.get(`/api/admin/clubs/`),
                authenticatedAxios.get(`/api/admin/courses/`)
            ]);
            // Ensure backend returns booking_system_type for clubs
            setClubs(clubsResponse.data || []);
            const coursesByClub = (coursesResponse.data || []).reduce((acc, course) => {
                (acc[course.club_id] = acc[course.club_id] || []).push(course);
                return acc;
            }, {});
            setCourses(coursesByClub);
        } catch (err) {
            // Check for auth error first
            if (handleAuthError(err)) return;
            
            setError('Failed to fetch data. Please ensure the backend is running and accessible.'); 
            console.error(err);
            setClubs([]); setCourses({}); // Reset on error
        } finally { 
            setLoading(false); 
        }
    }, [authenticatedAxios, onLogout, onClose]); // Add new dependencies

    useEffect(() => {
        if (open) {
            fetchData();
        } else {
            // Reset ALL state when modal closes
            setEditingClubId(null);
            setEditingCourseInfo(null);
            setExpandedClub(null);
            setError('');
            setClubFormError('');
            setCourseFormError('');
            setDiscoveringForClubId(null); // Reset discovery state
        }
    }, [open, fetchData]);

    const handleAccordionChange = (clubId) => (event, isExpanded) => {
        const newExpandedClubId = isExpanded ? clubId : null;
        const closingClubId = expandedClub; // The one that *was* open

        setExpandedClub(newExpandedClubId);

        // Close forms OR discovery tool if the accordion is closing OR a different one is opening
        if (!isExpanded || (closingClubId !== null && closingClubId !== clubId)) {
            // Determine which ID to check for active forms/discovery (the one being closed)
            const idToCheck = closingClubId !== null ? closingClubId : clubId;

            setEditingClubId(null);
            setEditingCourseInfo(null);
            setClubFormError('');
            setCourseFormError('');
            // Close discovery tool only if it belonged to the accordion being closed/switched from
            if (discoveringForClubId === idToCheck) {
                 setDiscoveringForClubId(null);
            }
        }
    };


    // --- Club Handlers ---
    const handleAddClubClick = () => {
        setEditingClubId('new_club');
        setEditingCourseInfo(null);
        setDiscoveringForClubId(null); // Close discovery
        setExpandedClub(null); // Collapse all accordions
        setClubFormError('');
         // Scroll modal to top
         const modalContent = document.querySelector('.admin-modal-content'); // Use class selector
         if (modalContent) modalContent.scrollTo(0, 0);
    };

    const handleEditClubClick = (clubId) => {
        setEditingClubId(clubId);
        setEditingCourseInfo(null);
        setDiscoveringForClubId(null); // Close discovery
        setExpandedClub(clubId); // Expand the accordion for the club being edited
        setClubFormError('');
    };

    const handleSaveClub = async (clubId, payload) => {
        setSavingLoading(true); // Use savingLoading
        setClubFormError(''); setError('');
        const url = clubId ? `/api/admin/clubs/${clubId}` : `/api/admin/clubs/`;
        const method = clubId ? 'put' : 'post';

        // Log the payload being sent
        console.log(`Saving club (${method.toUpperCase()}) to ${url} with payload:`, payload);

        try {
             // Ensure the payload matches backend schema (includes booking_system_type now)
             const response = await authenticatedAxios[method](url, payload);
             const savedClubId = clubId || response.data.id; // Get ID of newly created club

             setEditingClubId(null); // Close form on success
             await fetchData(); // Use await to ensure data is fresh before setting expansion

             // Keep the accordion expanded after saving
             setExpandedClub(savedClubId);

        } catch (err) {
            // Check for auth error first
            if (handleAuthError(err)) return;
            
            const errorMsg = `Failed to save club: ${err.response?.data?.detail || err.message}`;
            setClubFormError(errorMsg); // Set error specific to the club form
            console.error(err);
        } finally { 
            setSavingLoading(false); 
        } // Use savingLoading
    };

    const handleCancelClubEdit = () => {
        setEditingClubId(null);
        setClubFormError('');
        // Decide if the accordion should collapse or stay open - current logic keeps it open if it was an edit
    };

    const handleDeleteClub = async (clubId) => {
        const clubToDelete = clubs.find(c => c.id === clubId);
        const associatedCourses = courses[clubId] || [];
        const courseCount = associatedCourses.length;
        const confirmMessage = `Are you sure you want to delete the club "${clubToDelete?.name || 'ID: '+clubId}"? ` +
                               (courseCount > 0
                                ? `This will also delete ${courseCount} associated course(s). This action cannot be undone.`
                                : 'This action cannot be undone.');


        if (window.confirm(confirmMessage)) {
            setLoading(true); setError(''); // Use general loading for delete
            try {
                // NOTE: Backend should handle cascading delete or return error if courses exist and cascade is off.
                await authenticatedAxios.delete(`/api/admin/clubs/${clubId}`);
                setExpandedClub(null); // Collapse if it was open
                fetchData(); // Refresh list (will remove club and its courses from state)
            } catch (err) {
                // Check for auth error first
                if (handleAuthError(err)) return;
                
                // Use general error display for deletions
                setError(`Failed to delete club: ${err.response?.data?.detail || err.message}`);
                console.error(err);
            } finally { 
                setLoading(false); 
            } // Use general loading
        }
    };

    // --- Course Handlers ---
    const handleAddCourseClick = (clubId) => {
        setEditingCourseInfo({ clubId, courseId: 'new_course' });
        setEditingClubId(null); // Ensure club form is closed
        setDiscoveringForClubId(null); // Close discovery
        setExpandedClub(clubId); // Keep the accordion expanded
        setCourseFormError('');
    };

    const handleEditCourseClick = (clubId, courseId) => {
        setEditingCourseInfo({ clubId, courseId });
        setEditingClubId(null); // Ensure club form is closed
        setDiscoveringForClubId(null); // Close discovery
        setExpandedClub(clubId); // Keep the accordion expanded
        setCourseFormError('');
    };

    const handleSaveCourse = async (courseId, payload) => {
        setSavingLoading(true); // Use savingLoading
        setCourseFormError(''); setError('');
        const url = courseId ? `/api/admin/courses/${courseId}` : `/api/admin/courses/`;
        const method = courseId ? 'put' : 'post';

         // Log the payload being sent
         console.log(`Saving course (${method.toUpperCase()}) to ${url} with payload:`, payload);

        try {
            await authenticatedAxios[method](url, payload);
            setEditingCourseInfo(null); // Close form on success
            await fetchData(); // Refresh data
             // Keep the parent accordion expanded
             setExpandedClub(payload.club_id);
        } catch (err) {
            // Check for auth error first
            if (handleAuthError(err)) return;
            
            const errorMsg = `Failed to save course: ${err.response?.data?.detail || err.message}`;
            setCourseFormError(errorMsg); // Set error specific to the course form
            console.error(err);
        } finally { 
            setSavingLoading(false); 
        } // Use savingLoading
    };

    const handleCancelCourseEdit = () => {
        setEditingCourseInfo(null);
        setCourseFormError('');
        // Keep accordion expanded
    };

    const handleDeleteCourse = async (courseId) => {
         const courseToDelete = Object.values(courses).flat().find(c => c.id === courseId);
        if (window.confirm(`Are you sure you want to delete the course "${courseToDelete?.name || 'ID: '+courseId}"? This action cannot be undone.`)) {
            setLoading(true); setError(''); // Use general loading for delete
             let clubIdToKeepExpanded = null;
             // Find the clubId this course belongs to *before* deleting & refetching
             for (const cId in courses) {
                 if (courses[cId].some(c => c.id === courseId)) {
                     clubIdToKeepExpanded = parseInt(cId, 10);
                     break;
                 }
             }

            try {
                await authenticatedAxios.delete(`/api/admin/courses/${courseId}`);
                await fetchData(); // Refresh list
                // If we found the club ID before, re-expand it after fetch
                if (clubIdToKeepExpanded !== null) {
                     setExpandedClub(clubIdToKeepExpanded);
                }

            } catch (err) {
                // Check for auth error first
                if (handleAuthError(err)) return;
                
                // Use general error display for deletions for now
                setError(`Failed to delete course: ${err.response?.data?.detail || err.message}`);
                console.error(err);
            } finally { 
                setLoading(false); 
            } // Use general loading
        }
    };

    // --- Discovery Handlers ---
    const handleOpenDiscoveryTool = (clubId) => {
        // Find the club to check if discovery is possible
        const club = clubs.find(c => c.id === clubId);
        if (club && club.booking_system_type && club.api_identifier) {
             setDiscoveringForClubId(clubId);
             setEditingClubId(null); // Close other forms
             setEditingCourseInfo(null);
             setExpandedClub(clubId); // Ensure accordion is open
             setClubFormError(''); // Clear form errors
             setCourseFormError('');
        } else {
            // This case should ideally be prevented by disabling the button,
            // but handle it just in case.
            setError("Cannot auto-discover: Club needs both a Booking System Type and an API Identifier set.");
            console.warn("Discovery attempt on club without required fields:", club);
        }
    };

     const handleCancelDiscovery = () => {
         setDiscoveringForClubId(null);
         // Keep the accordion open by default
     };


     const handleBulkAddCourses = async (clubId, coursesToAdd) => {
        if (!coursesToAdd || coursesToAdd.length === 0) return;

        console.log(`Attempting to bulk add ${coursesToAdd.length} courses for club ${clubId}`);
        setSavingLoading(true);
        setError('');

        const addPromises = coursesToAdd.map(course => {
            // Construct payload, omitting keys intended to be default/null
            const payload = {
                name: course.name,
                club_id: clubId,
                holes: course.holes,
                // api_identifier and api_details can be null/empty if not provided
                api_identifier: course.api_identifier || null,
                api_details: course.api_details || {},
            };

            console.log("Adding course with payload:", payload);
            return authenticatedAxios.post(`/api/admin/courses/`, payload);
        });

        try {
            const results = await Promise.allSettled(addPromises);
            console.log("Bulk add results:", results);

            const successfulAdds = results.filter(r => r.status === 'fulfilled').length;
            const failedAdds = results.filter(r => r.status === 'rejected');

            if (failedAdds.length > 0) {
                // Check for auth error in first failure
                const firstError = failedAdds[0].reason;
                if (handleAuthError(firstError)) return;
                
                // Try to get more specific backend error message
                let errorDetail = 'Unknown error';
                if (firstError.response) {
                    // FastAPI often puts detailed errors in response.data.detail
                    if (typeof firstError.response.data?.detail === 'string') {
                        errorDetail = firstError.response.data.detail;
                    } else if (Array.isArray(firstError.response.data?.detail)) {
                         // Handle validation errors which might be arrays of objects
                         try {
                             errorDetail = firstError.response.data.detail.map(d => `${d.loc?.join('.')}: ${d.msg}`).join('; ');
                         } catch {
                             errorDetail = JSON.stringify(firstError.response.data.detail);
                         }
                    } else {
                         errorDetail = firstError.message;
                    }
                } else {
                    errorDetail = firstError.message;
                }

                setError(`Added ${successfulAdds} course(s). Failed to add ${failedAdds.length}. First error: ${errorDetail}`);
                console.error("Bulk add failures:", failedAdds.map(f => f.reason));
            } else {
                console.log(`Successfully added ${coursesToAdd.length} courses.`);
            }

            setDiscoveringForClubId(null);
            await fetchData();
            setExpandedClub(clubId);

        } catch (err) {
            // Check for auth error first
            if (handleAuthError(err)) return;
            
            setError(`An unexpected error occurred during bulk add: ${err.message}`);
            console.error("Bulk Add Unexpected Error:", err);
            await fetchData(); // Still try to refresh
            setExpandedClub(clubId);
        } finally {
            setSavingLoading(false);
        }
    };

    // Memoize derived state for performance (optional, depends on list size)
    const currentEditingClub = editingClubId && editingClubId !== 'new_club'
        ? clubs.find(c => c.id === editingClubId)
        : null;

    // Find the course being edited
    const findEditingCourse = () => {
        if (editingCourseInfo && typeof editingCourseInfo.courseId === 'number') {
             const coursesForClub = courses[editingCourseInfo.clubId] || [];
             return coursesForClub.find(course => course.id === editingCourseInfo.courseId);
        }
        return null;
    };
    const currentEditingCourse = findEditingCourse();

    // Find the club associated with the discovery tool
    const currentDiscoveryClub = discoveringForClubId
        ? clubs.find(c => c.id === discoveringForClubId)
        : null;
        
    // Need to modify CourseDiscoveryTool to accept authenticatedAxios
    const courseDiscoveryProps = {
        club: currentDiscoveryClub,
        existingCourses: currentDiscoveryClub ? (courses[currentDiscoveryClub.id] || []) : [],
        onAddCourses: (discovered) => handleBulkAddCourses(discoveringForClubId, discovered),
        onCancel: handleCancelDiscovery,
        authenticatedAxios // Pass authenticated axios to discovery tool
    };

    return (
        <Modal open={open} onClose={onClose} aria-labelledby="admin-modal-title">
            {/* Use class for potential scrolling querySelector */}
            <Box sx={modalStyle} className="admin-modal-content">
                <Typography variant="h5" gutterBottom id="admin-modal-title">Manage Clubs & Courses</Typography>
                <Divider sx={{ my: 2 }} />

                {/* Global Error Display for Fetch/Delete/Bulk Add */}
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {/* --- Add New Club Button & Form --- */}
                <Box sx={{ mb: 2 }}>
                    {/* Only show Add button when no forms or discovery tool are open ANYWHERE */}
                     {!editingClubId && !editingCourseInfo && !discoveringForClubId && (
                         <Button
                             startIcon={<Add />}
                             onClick={handleAddClubClick}
                             variant="contained"
                             disabled={isAnyLoading} // Use combined loading state
                             sx={{ mb: 1 }}
                         >
                             Add New Golf Club
                         </Button>
                     )}
                    {/* Show Add New Club Form */}
                    {editingClubId === 'new_club' && (
                        <ClubForm
                            onSave={handleSaveClub}
                            onCancel={handleCancelClubEdit}
                            loading={savingLoading} // Use savingLoading for form actions
                            error={clubFormError} // Pass specific form error
                        />
                    )}
                </Box>

                {/* Loading Indicator for initial fetch */}
                {loading && !clubs.length && <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}><CircularProgress /></Box>}

                {/* --- Clubs and Courses List --- */}
                {clubs.length === 0 && !loading && editingClubId !== 'new_club' && <Typography sx={{ my: 2, textAlign: 'center' }}>No golf clubs found. Click 'Add New Golf Club' to start.</Typography>}

                {clubs.map((club) => {
                    const clubCourses = courses[club.id] || [];
                    const isEditingThisClub = editingClubId === club.id;
                    const isAddingCourseToThisClub = editingCourseInfo?.clubId === club.id && editingCourseInfo?.courseId === 'new_course';
                    const isDiscoveringForThisClub = discoveringForClubId === club.id;
                    const isEditingAnyCourseInThisClub = editingCourseInfo?.clubId === club.id && typeof editingCourseInfo?.courseId === 'number';

                    // Determine if *any* form or the discovery tool is active within this specific accordion
                    const isFormOrDiscoveryActiveInThisAccordion = isEditingThisClub || isAddingCourseToThisClub || isEditingAnyCourseInThisClub || isDiscoveringForThisClub;

                    // Check if discovery is possible for this club
                    const canDiscover = !!(club.booking_system_type && club.api_identifier);
                    const discoveryDisabledReason = !canDiscover ? "Club needs Booking System and API Identifier set" : "";

                    return (
                        <Accordion
                            key={club.id}
                            expanded={expandedClub === club.id}
                            onChange={handleAccordionChange(club.id)}
                            disabled={loading} // Disable expanding/collapsing only during initial fetch
                            sx={{
                                 my: 1,
                                 '&.Mui-expanded': { my: 1 }, // Consistent margin when expanded
                                 '& .MuiAccordionSummary-root.Mui-disabled': { opacity: 1 }, // Prevent summary fade when disabled
                                 '& .MuiAccordionSummary-content.Mui-disabled': { opacity: 0.6 } // Fade content slightly
                             }}
                            TransitionProps={{ unmountOnExit: true }} // Optimize rendering
                        >
                            {/* ==== ACCORDION SUMMARY ==== */}
                            <AccordionSummary
                                expandIcon={<ExpandMore />}
                                aria-controls={`club-${club.id}-content`}
                                id={`club-${club.id}-header`}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', flexWrap: 'nowrap', overflow: 'hidden' }}>
                                    <Business sx={{ mr: 1, color: 'action.active', flexShrink: 0 }} />
                                    <Typography sx={{ flexGrow: 1, fontWeight: 'medium', mr: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {club.name}
                                        <Typography component="span" variant="caption" color="textSecondary" sx={{ ml: 0.5 }}> (ID: {club.id})</Typography>
                                        {/* Display Club's Booking System */}
                                        {club.booking_system_type && (
                                            <Typography component="span" variant="caption" color="primary.main" sx={{ ml: 1, fontWeight: 'medium' }}>
                                                [{SUPPORTED_BOOKING_SYSTEMS.find(s => s.key === club.booking_system_type)?.label || club.booking_system_type}]
                                            </Typography>
                                        )}
                                    </Typography>

                                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, ml: 1 }}>
                                        {clubCourses.length} course{clubCourses.length !== 1 ? 's' : ''}
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            {/* ==== ACCORDION DETAILS ==== */}
                            <AccordionDetails
                                sx={{ pt: 0, borderTop: '1px solid', borderColor: 'divider' }}
                                id={`club-${club.id}-content`} // Add id for aria-controls
                            >

                                {/* --- Club Action Buttons (Displayed when expanded and no form/discovery is active) --- */}
                                 {!isFormOrDiscoveryActiveInThisAccordion && (
                                     <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1, pt: 2, mb: 2 }}> {/* Added flexWrap */}
                                         {/* Add Course Discovery Button */}
                                         <Tooltip title={canDiscover ? "Automatically find courses for this club" : discoveryDisabledReason}>
                                            {/* Need span for tooltip when button is disabled */}
                                            <span>
                                                 <Button
                                                    size="small"
                                                    startIcon={<FindInPage />}
                                                    onClick={() => handleOpenDiscoveryTool(club.id)}
                                                    disabled={isAnyLoading || !canDiscover} // Disable if loading or missing fields
                                                    color="info"
                                                >
                                                     Auto-Discover Courses
                                                 </Button>
                                            </span>
                                         </Tooltip>
                                         <Tooltip title="Manually add a new course to this club">
                                              <Button size="small" startIcon={<Add />} onClick={() => handleAddCourseClick(club.id)} disabled={isAnyLoading} color="secondary">
                                                  Add Course Manually
                                              </Button>
                                         </Tooltip>
                                         <Tooltip title="Edit this club's details">
                                              <Button size="small" startIcon={<Edit />} onClick={() => handleEditClubClick(club.id)} disabled={isAnyLoading}>
                                                  Edit Club
                                              </Button>
                                         </Tooltip>
                                         <Tooltip title="Delete this club and potentially its courses">
                                              <Button size="small" startIcon={<Delete />} onClick={() => handleDeleteClub(club.id)} disabled={isAnyLoading} color="error">
                                                  Delete Club
                                              </Button>
                                         </Tooltip>
                                     </Box>
                                 )}

                                {/* --- Club Edit Form --- */}
                                {isEditingThisClub && (
                                     <Box sx={{ pt: 2 }}> {/* Add padding top */}
                                         <ClubForm
                                             club={currentEditingClub}
                                             onSave={handleSaveClub}
                                             onCancel={handleCancelClubEdit}
                                             loading={savingLoading} // Use savingLoading for form actions
                                             error={clubFormError} // Pass specific form error
                                         />
                                     </Box>
                                )}

                                {/* --- Course Discovery Tool --- */}
                                {isDiscoveringForThisClub && currentDiscoveryClub && ( // Ensure club data is available
                                     <Box sx={{ pt: 1 }}> {/* Less padding than forms */}
                                         <CourseDiscoveryTool
                                             {...courseDiscoveryProps}
                                         />
                                     </Box>
                                )}

                                {/* --- Course Add Form (Manual) --- */}
                                {isAddingCourseToThisClub && (
                                     <Box sx={{ pt: 2 }}> {/* Add padding top */}
                                         <CourseForm
                                             clubId={club.id}
                                             parentClubBookingSystem={club.booking_system_type} // Pass club's system
                                             onSave={handleSaveCourse}
                                             onCancel={handleCancelCourseEdit}
                                             loading={savingLoading} // Use savingLoading for form actions
                                             error={courseFormError} // Pass specific form error
                                         />
                                     </Box>
                                )}

                                {/* --- List of Courses --- */}
                                {clubCourses.length > 0 ? (
                                     // Add top margin only if NO form/tool is active (action buttons were shown)
                                     <List dense disablePadding sx={{ mt: !isFormOrDiscoveryActiveInThisAccordion ? 0 : 2 }}>
                                         {clubCourses.map((course) => {
                                             const isEditingThisCourse = editingCourseInfo?.clubId === club.id && editingCourseInfo?.courseId === course.id;
                                             // Determine the effective booking system for display
                                             const displayBookingSystemKey = course.booking_system_type || club.booking_system_type;
                                             const displayBookingSystemLabel = SUPPORTED_BOOKING_SYSTEMS.find(s => s.key === displayBookingSystemKey)?.label || displayBookingSystemKey;
                                             const isInherited = !course.booking_system_type && club.booking_system_type;

                                             return (
                                                 <React.Fragment key={course.id}>
                                                     <ListItem
                                                          sx={{
                                                               pl: 2, // Indent courses
                                                               borderLeft: '3px solid',
                                                               borderColor: 'divider',
                                                               mb: 1, // Space between course items
                                                               backgroundColor: isEditingThisCourse ? 'action.hover' : 'transparent', // Highlight if editing
                                                               position: 'relative', // Needed for absolute positioning of actions
                                                               // Adjust paddingRight only if NO form/discovery is active in *this* accordion to make space for buttons
                                                               pr: !isFormOrDiscoveryActiveInThisAccordion ? '80px' : 2, // Approx width for 2 small IconButtons + gap
                                                           }}
                                                     >
                                                         <GolfCourse sx={{ mr: 1, fontSize: '1.1rem', color: 'action.active', flexShrink: 0 }} />
                                                         <ListItemText
                                                              primary={`${course.name} (${course.holes} holes)`}
                                                              secondary={
                                                                  `ID: ${course.id}` +
                                                                  (displayBookingSystemKey ? ` • System: ${displayBookingSystemLabel}${isInherited ? ' (Inherited)' : ''}` : '') +
                                                                  (course.api_identifier ? ` • API ID: ${course.api_identifier}` : '')
                                                              }
                                                              primaryTypographyProps={{ variant: 'body2', sx: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
                                                              secondaryTypographyProps={{ variant: 'caption', sx: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }} // Prevent long secondary text wrap
                                                          />
                                                         {/* Course Action Buttons - Absolutely Positioned */}
                                                         {/* Show only if NO form/discovery is active in this accordion */}
                                                         {!isFormOrDiscoveryActiveInThisAccordion && (
                                                              <Box sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', display: 'flex', gap: 0.5 }}>
                                                                  <Tooltip title="Edit Course">
                                                                       {/* Disable if any save/load is happening */}
                                                                      <span> {/* Span needed for tooltip when button is disabled */}
                                                                          <IconButton size="small" aria-label="edit-course" onClick={() => handleEditCourseClick(club.id, course.id)} disabled={isAnyLoading}>
                                                                              <Edit fontSize="inherit" />
                                                                          </IconButton>
                                                                      </span>
                                                                  </Tooltip>
                                                                  <Tooltip title="Delete Course">
                                                                       {/* Disable if any save/load is happening */}
                                                                      <span> {/* Span needed for tooltip when button is disabled */}
                                                                          <IconButton size="small" aria-label="delete-course" onClick={() => handleDeleteCourse(course.id)} disabled={isAnyLoading}>
                                                                              <Delete fontSize="inherit" />
                                                                          </IconButton>
                                                                      </span>
                                                                  </Tooltip>
                                                              </Box>
                                                         )}
                                                     </ListItem>

                                                     {/* Course Edit Form - Rendered below the item */}
                                                     {isEditingThisCourse && currentEditingCourse && ( // Ensure course data is available
                                                         <Box sx={{ pl: 2, mb: 1 }}> {/* Indent form to align */}
                                                             <CourseForm
                                                                 course={currentEditingCourse}
                                                                 clubId={club.id} // Pass clubId just in case
                                                                 parentClubBookingSystem={club.booking_system_type} // Pass club's system
                                                                 onSave={handleSaveCourse}
                                                                 onCancel={handleCancelCourseEdit}
                                                                 loading={savingLoading} // Use savingLoading for form actions
                                                                 error={courseFormError} // Pass specific form error
                                                             />
                                                         </Box>
                                                     )}
                                                 </React.Fragment>
                                             );
                                         })}
                                     </List>
                                ) : (
                                     // Show "No courses" only if not editing/adding/discovering
                                     !isFormOrDiscoveryActiveInThisAccordion &&
                                     <Typography variant="body2" color="textSecondary" sx={{ pl: 2, mt: 2, fontStyle: 'italic' }}> {/* Add top margin */}
                                         No courses configured for this club yet. Use buttons above to add manually or auto-discover.
                                     </Typography>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    );
                })}

                {/* Spacer before close button if content is short */}
                <Box sx={{ flexGrow: 1 }} />

                <Divider sx={{ my: 2 }} />
                {/* Disable close if any operation (fetch, save, delete) is in progress */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Button 
                        color="error" 
                        onClick={onLogout} 
                        disabled={isAnyLoading}
                    >
                        Logout
                    </Button>
                    <Button 
                        onClick={onClose} 
                        sx={{ mt: 1 }} 
                        disabled={isAnyLoading}
                    >
                        Close
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}

export default AdminModal;